import React, { useState, useEffect, useCallback } from 'react';
import { 
  Activity, Heart, Moon, Footprints, Flame, Droplets, Scale, 
  Target, TrendingUp, Watch, Smartphone, Plus, Settings, 
  RefreshCw, CheckCircle, XCircle, ChevronRight, Calendar,
  Zap, Clock, Award, BarChart3
} from 'lucide-react';
import { useUserId } from '../context/UserAuthContext';
import { toast } from 'sonner';

const getApiUrl = () => window.location.origin;

const HealthPage = () => {
  const userId = useUserId();
  
  // State
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [goals, setGoals] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    steps: '',
    weight_kg: '',
    sleep_hours: '',
    water_ml: '',
    calories_burned: '',
    heart_rate_avg: '',
    notes: ''
  });

  // Fetch health summary
  const fetchSummary = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/terra-health/summary?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setSummary(data);
        setGoals(data.goals || {});
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [userId]);

  // Fetch daily data
  const fetchDailyData = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/terra-health/daily?user_id=${userId}&days=14`);
      const data = await response.json();
      if (data.success) {
        setDailyData(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching daily data:', error);
    }
  }, [userId]);

  // Fetch sleep data
  const fetchSleepData = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/terra-health/sleep?user_id=${userId}&days=7`);
      const data = await response.json();
      if (data.success) {
        setSleepData(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching sleep data:', error);
    }
  }, [userId]);

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/terra-health/activities?user_id=${userId}&days=30`);
      const data = await response.json();
      if (data.success) {
        setActivities(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [userId]);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSummary(),
        fetchDailyData(),
        fetchSleepData(),
        fetchActivities()
      ]);
      setLoading(false);
    };
    
    if (userId) {
      loadData();
    }
  }, [userId, fetchSummary, fetchDailyData, fetchSleepData, fetchActivities]);

  // Connect device
  const handleConnectDevice = async () => {
    if (!userId) {
      toast.error('Please login first');
      return;
    }
    
    try {
      const response = await fetch(`${getApiUrl()}/api/terra-health/connect?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          success_redirect_url: `${window.location.origin}/health?connected=true`,
          failure_redirect_url: `${window.location.origin}/health?connected=false`
        })
      });
      
      const data = await response.json();
      
      if (data.requires_setup) {
        toast.error('Terra API not configured. Contact admin to set up health integrations.');
        return;
      }
      
      if (data.success && data.widget_url) {
        window.location.href = data.widget_url;
      } else {
        toast.error(data.error || 'Failed to connect device');
      }
    } catch (error) {
      toast.error('Failed to connect device');
    }
  };

  // Save manual entry
  const handleSaveManualEntry = async () => {
    if (!userId) return;
    
    try {
      const entry = {
        date: manualEntry.date,
        steps: manualEntry.steps ? parseInt(manualEntry.steps) : null,
        weight_kg: manualEntry.weight_kg ? parseFloat(manualEntry.weight_kg) : null,
        sleep_hours: manualEntry.sleep_hours ? parseFloat(manualEntry.sleep_hours) : null,
        water_ml: manualEntry.water_ml ? parseInt(manualEntry.water_ml) : null,
        calories_burned: manualEntry.calories_burned ? parseInt(manualEntry.calories_burned) : null,
        heart_rate_avg: manualEntry.heart_rate_avg ? parseInt(manualEntry.heart_rate_avg) : null,
        notes: manualEntry.notes || null
      };
      
      const response = await fetch(`${getApiUrl()}/api/terra-health/manual?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Entry saved!');
        setShowManualEntry(false);
        fetchSummary();
        fetchDailyData();
      }
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  // Save goals
  const handleSaveGoals = async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${getApiUrl()}/api/terra-health/goals?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goals)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Goals saved!');
        setShowGoalsModal(false);
      }
    } catch (error) {
      toast.error('Failed to save goals');
    }
  };

  // Format numbers
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  // Calculate progress percentage
  const calculateProgress = (current, goal) => {
    if (!goal || !current) return 0;
    return Math.min(100, Math.round((current / goal) * 100));
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Stat Card Component
  const StatCard = ({ icon: Icon, label, value, unit, goal, color, trend }) => {
    const progress = goal ? calculateProgress(parseFloat(value) || 0, goal) : null;
    
    return (
      <div className="bg-black border border-gray-700 rounded-xl p-4 hover:border-gray-500 hover:shadow-[0_0_15px_rgba(156,163,175,0.2)] transition-all duration-300">
        <div className="flex items-start justify-between mb-2">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon size={20} className="text-white" />
          </div>
          {trend && (
            <span className={`text-xs px-2 py-0.5 rounded ${trend > 0 ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          )}
        </div>
        <p className="text-gray-400 text-sm mb-1">{label}</p>
        <p className="text-2xl font-bold">
          {formatNumber(value)}
          {unit && <span className="text-sm text-gray-400 ml-1">{unit}</span>}
        </p>
        {progress !== null && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{progress}% of goal</span>
              <span>{formatNumber(goal)}</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full transition-all duration-500 ${progress >= 100 ? 'bg-green-500' : 'bg-purple-500'}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Supported Devices List
  const supportedDevices = [
    { name: 'Apple Health', icon: '🍎' },
    { name: 'Google Fit', icon: '🔵' },
    { name: 'Fitbit', icon: '💚' },
    { name: 'Garmin', icon: '🔷' },
    { name: 'WHOOP', icon: '⚫' },
    { name: 'Oura Ring', icon: '💍' },
    { name: 'Withings', icon: '⚪' },
    { name: 'Samsung Health', icon: '📱' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-6 flex items-center justify-center">
        <RefreshCw size={32} className="animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1">Health</h1>
          <p className="text-gray-400">Track your fitness, sleep, and wellness</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowManualEntry(true)}
            className="px-3 py-2 bg-black border border-gray-600 hover:border-gray-400 rounded-lg text-sm flex items-center gap-2 transition-all"
          >
            <Plus size={16} />
            Log Entry
          </button>
          <button
            onClick={() => setShowGoalsModal(true)}
            className="px-3 py-2 bg-black border border-gray-600 hover:border-gray-400 rounded-lg text-sm flex items-center gap-2 transition-all"
          >
            <Target size={16} />
            Goals
          </button>
        </div>
      </div>

      {/* Connection Status */}
      {!summary?.connected_device ? (
        <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                <Watch size={24} className="text-purple-400" />
                Connect Your Wearable
              </h2>
              <p className="text-gray-300 mb-4">
                Sync your health data automatically from your favorite wearable device or app.
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                {supportedDevices.map((device) => (
                  <span key={device.name} className="px-3 py-1 bg-black/50 rounded-full text-sm flex items-center gap-1">
                    <span>{device.icon}</span>
                    {device.name}
                  </span>
                ))}
              </div>
              <button
                onClick={handleConnectDevice}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium flex items-center gap-2 transition-all"
              >
                <Smartphone size={18} />
                Connect Device
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 mb-6 px-4 py-3 bg-green-900/20 border border-green-700/50 rounded-lg">
          <CheckCircle size={20} className="text-green-400" />
          <span className="text-green-300">
            Connected to <strong className="capitalize">{summary.connected_device}</strong>
          </span>
          {summary.last_sync && (
            <span className="text-gray-400 text-sm ml-auto">
              Last sync: {new Date(summary.last_sync).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'activity', label: 'Activity', icon: Activity },
          { id: 'sleep', label: 'Sleep', icon: Moon },
          { id: 'history', label: 'History', icon: Calendar },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 border whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-black border-white text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                : 'bg-black border-gray-700 text-gray-400 hover:border-gray-500'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Today's Stats */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Today's Progress</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Footprints}
                label="Steps"
                value={summary?.today?.steps || 0}
                goal={goals.daily_steps}
                color="bg-blue-600"
              />
              <StatCard
                icon={Flame}
                label="Calories Burned"
                value={summary?.today?.calories_burned || 0}
                unit="kcal"
                goal={goals.daily_calories}
                color="bg-orange-600"
              />
              <StatCard
                icon={Heart}
                label="Avg Heart Rate"
                value={summary?.today?.heart_rate_avg || '--'}
                unit="bpm"
                color="bg-red-600"
              />
              <StatCard
                icon={Zap}
                label="Active Minutes"
                value={summary?.today?.active_minutes || 0}
                unit="min"
                color="bg-green-600"
              />
            </div>
          </div>

          {/* Weekly Summary */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Weekly Averages</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-black border border-gray-700 rounded-xl p-4 text-center">
                <Footprints size={24} className="mx-auto mb-2 text-blue-400" />
                <p className="text-2xl font-bold">{formatNumber(summary?.weekly_averages?.steps || 0)}</p>
                <p className="text-sm text-gray-400">Avg Steps/Day</p>
              </div>
              <div className="bg-black border border-gray-700 rounded-xl p-4 text-center">
                <Flame size={24} className="mx-auto mb-2 text-orange-400" />
                <p className="text-2xl font-bold">{formatNumber(summary?.weekly_averages?.calories || 0)}</p>
                <p className="text-sm text-gray-400">Avg Calories/Day</p>
              </div>
              <div className="bg-black border border-gray-700 rounded-xl p-4 text-center">
                <Activity size={24} className="mx-auto mb-2 text-green-400" />
                <p className="text-2xl font-bold">{summary?.weekly_averages?.active_minutes || 0}</p>
                <p className="text-sm text-gray-400">Avg Active Min</p>
              </div>
            </div>
          </div>

          {/* Last Night's Sleep */}
          {summary?.latest_sleep && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Last Night's Sleep</h2>
              <div className="bg-black border border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Moon size={24} className="text-indigo-400" />
                    <div>
                      <p className="text-xl font-bold">
                        {formatDuration(summary.latest_sleep.duration_seconds)}
                      </p>
                      <p className="text-sm text-gray-400">Total Sleep</p>
                    </div>
                  </div>
                  {summary.latest_sleep.sleep_score && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-400">{summary.latest_sleep.sleep_score}%</p>
                      <p className="text-sm text-gray-400">Sleep Score</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-gray-900 rounded-lg p-2 text-center">
                    <p className="text-sm font-medium text-blue-300">
                      {formatDuration(summary.latest_sleep.deep_sleep_seconds)}
                    </p>
                    <p className="text-xs text-gray-500">Deep</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-2 text-center">
                    <p className="text-sm font-medium text-cyan-300">
                      {formatDuration(summary.latest_sleep.light_sleep_seconds)}
                    </p>
                    <p className="text-xs text-gray-500">Light</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-2 text-center">
                    <p className="text-sm font-medium text-purple-300">
                      {formatDuration(summary.latest_sleep.rem_sleep_seconds)}
                    </p>
                    <p className="text-xs text-gray-500">REM</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-2 text-center">
                    <p className="text-sm font-medium text-yellow-300">
                      {formatDuration(summary.latest_sleep.awake_seconds)}
                    </p>
                    <p className="text-xs text-gray-500">Awake</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Recent Workouts</h2>
          {activities.length > 0 ? (
            activities.map((activity, idx) => (
              <div key={idx} className="bg-black border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-600/20 rounded-lg">
                      <Activity size={20} className="text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium capitalize">{activity.activity_type || 'Workout'}</p>
                      <p className="text-sm text-gray-400">
                        {activity.start_time ? new Date(activity.start_time).toLocaleDateString() : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatDuration(activity.duration_seconds)}</p>
                    <p className="text-sm text-gray-400">{activity.calories_burned || 0} kcal</p>
                  </div>
                </div>
                {activity.distance_meters && (
                  <div className="mt-3 pt-3 border-t border-gray-800 flex gap-4 text-sm text-gray-400">
                    <span>{(activity.distance_meters / 1000).toFixed(2)} km</span>
                    {activity.heart_rate_avg && <span>{activity.heart_rate_avg} bpm avg</span>}
                    {activity.steps && <span>{formatNumber(activity.steps)} steps</span>}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-black border border-gray-700 rounded-xl">
              <Activity size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No workouts recorded yet</p>
              <p className="text-sm text-gray-500">Connect a device or log an entry manually</p>
            </div>
          )}
        </div>
      )}

      {/* Sleep Tab */}
      {activeTab === 'sleep' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Sleep History</h2>
          {sleepData.length > 0 ? (
            sleepData.map((sleep, idx) => (
              <div key={idx} className="bg-black border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Moon size={20} className="text-indigo-400" />
                    <span className="font-medium">{sleep.date}</span>
                  </div>
                  <span className="text-xl font-bold">{formatDuration(sleep.duration_seconds)}</span>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 bg-blue-900/30 rounded h-2" title="Deep Sleep">
                    <div className="bg-blue-500 h-full rounded" style={{ width: `${(sleep.deep_sleep_seconds / sleep.duration_seconds) * 100}%` }} />
                  </div>
                  <div className="flex-1 bg-cyan-900/30 rounded h-2" title="Light Sleep">
                    <div className="bg-cyan-500 h-full rounded" style={{ width: `${(sleep.light_sleep_seconds / sleep.duration_seconds) * 100}%` }} />
                  </div>
                  <div className="flex-1 bg-purple-900/30 rounded h-2" title="REM Sleep">
                    <div className="bg-purple-500 h-full rounded" style={{ width: `${(sleep.rem_sleep_seconds / sleep.duration_seconds) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-black border border-gray-700 rounded-xl">
              <Moon size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No sleep data recorded yet</p>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Daily History</h2>
          {dailyData.length > 0 ? (
            dailyData.slice().reverse().map((day, idx) => (
              <div key={idx} className="bg-black border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition-all">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{day.date}</span>
                  <span className="text-sm text-gray-400">{day.source === 'manual' ? 'Manual Entry' : 'Wearable'}</span>
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Steps</p>
                    <p className="font-medium">{formatNumber(day.steps || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Calories</p>
                    <p className="font-medium">{formatNumber(day.calories_burned || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Active Min</p>
                    <p className="font-medium">{day.active_minutes || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Heart Rate</p>
                    <p className="font-medium">{day.heart_rate_avg || '--'} bpm</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-black border border-gray-700 rounded-xl">
              <Calendar size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No history yet</p>
              <p className="text-sm text-gray-500">Start tracking to see your progress</p>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Log Health Entry</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Date</label>
                <input
                  type="date"
                  value={manualEntry.date}
                  onChange={(e) => setManualEntry({ ...manualEntry, date: e.target.value })}
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Steps</label>
                  <input
                    type="number"
                    value={manualEntry.steps}
                    onChange={(e) => setManualEntry({ ...manualEntry, steps: e.target.value })}
                    placeholder="10000"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Calories</label>
                  <input
                    type="number"
                    value={manualEntry.calories_burned}
                    onChange={(e) => setManualEntry({ ...manualEntry, calories_burned: e.target.value })}
                    placeholder="2000"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Weight (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={manualEntry.weight_kg}
                    onChange={(e) => setManualEntry({ ...manualEntry, weight_kg: e.target.value })}
                    placeholder="70.0"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Sleep (hours)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={manualEntry.sleep_hours}
                    onChange={(e) => setManualEntry({ ...manualEntry, sleep_hours: e.target.value })}
                    placeholder="8.0"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Water (ml)</label>
                  <input
                    type="number"
                    value={manualEntry.water_ml}
                    onChange={(e) => setManualEntry({ ...manualEntry, water_ml: e.target.value })}
                    placeholder="2500"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Avg Heart Rate</label>
                  <input
                    type="number"
                    value={manualEntry.heart_rate_avg}
                    onChange={(e) => setManualEntry({ ...manualEntry, heart_rate_avg: e.target.value })}
                    placeholder="72"
                    className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Notes</label>
                <textarea
                  value={manualEntry.notes}
                  onChange={(e) => setManualEntry({ ...manualEntry, notes: e.target.value })}
                  placeholder="How are you feeling today?"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white h-20"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowManualEntry(false)}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveManualEntry}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-all"
              >
                Save Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goals Modal */}
      {showGoalsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Set Your Goals</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Daily Steps Goal</label>
                <input
                  type="number"
                  value={goals.daily_steps || ''}
                  onChange={(e) => setGoals({ ...goals, daily_steps: parseInt(e.target.value) || 10000 })}
                  placeholder="10000"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Daily Calories Goal</label>
                <input
                  type="number"
                  value={goals.daily_calories || ''}
                  onChange={(e) => setGoals({ ...goals, daily_calories: parseInt(e.target.value) || 2000 })}
                  placeholder="2000"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Daily Sleep Goal (hours)</label>
                <input
                  type="number"
                  step="0.5"
                  value={goals.daily_sleep_hours || ''}
                  onChange={(e) => setGoals({ ...goals, daily_sleep_hours: parseFloat(e.target.value) || 8 })}
                  placeholder="8"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Daily Water Goal (ml)</label>
                <input
                  type="number"
                  value={goals.daily_water_ml || ''}
                  onChange={(e) => setGoals({ ...goals, daily_water_ml: parseInt(e.target.value) || 2500 })}
                  placeholder="2500"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Target Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={goals.target_weight_kg || ''}
                  onChange={(e) => setGoals({ ...goals, target_weight_kg: parseFloat(e.target.value) || null })}
                  placeholder="70.0"
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowGoalsModal(false)}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGoals}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-all"
              >
                Save Goals
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthPage;
